import { Menu, MenuItem, MenuList, MenuPopover, MenuTrigger } from '@fluentui/react-components';
import React from 'react';
import {
    ArrowExport20Regular,
    Delete20Regular,
    EditArrowBack16Regular,
    MoreVertical20Regular,
    TableSimple20Regular,
    TaskListSquareAdd20Regular,
    TextAlignLeft20Regular,
    TextBulletList20Regular,
    Wand20Regular,
} from '@fluentui/react-icons';

// Custom Undo button icon component for Quill editor
const CustomUndo = () => (
    <svg viewBox="0 0 18 18">
        <polygon className="ql-fill ql-stroke" points="6 10 4 12 2 10 6 10" />
        <path className="ql-stroke" d="M8.09,13.91A4.6,4.6,0,0,0,9,14,5,5,0,1,0,4,9" />
    </svg>
);

// Redo button icon component for Quill editor
const CustomRedo = () => (
    <svg viewBox="0 0 18 18">
        <polygon className="ql-fill ql-stroke" points="12 10 14 12 16 10 12 10" />
        <path className="ql-stroke" d="M9.91,13.91A4.6,4.6,0,0,1,9,14a5,5,0,1,1,5-5" />
    </svg>
);

// Undo and redo functions for Custom Toolbar
function undoChange() {
    this.quill.history.undo();
}

function redoChange() {
    this.quill.history.redo();
}

// Modules object factory for setting up the Quill editor
export const getModules = (toolbarId = 'toolbar') => ({
    toolbar: {
        container: `#${toolbarId}`,
        handlers: {
            undo: undoChange,
            redo: redoChange,
        },
    },
    history: {
        delay: 1000,
        maxStack: 100,
        userOnly: true,
    },
});

// Formats objects for setting up the Quill editor
export const formats = ['header', 'bold', 'italic', 'underline', 'strike', 'list', 'bullet', 'script', 'indent', 'direction', 'link', 'image', 'blockquote', 'code-block', 'clean'];

// Quill Toolbar component
export const QuillToolbar = ({ sidebar = false, toolbarId = 'toolbar', handleEntryDeleteClick = () => {}, handleRewriteWithAI = () => {}, handleMoveEntry = () => {} }) => {
    const handleAIAction = mode => {
        handleRewriteWithAI(mode);
    };

    return (
        <div className="d-flex align-items-center justify-content-between">
            <div id={toolbarId}>
                {!sidebar && (
                    <span className="ql-formats">
                        <button className="ql-undo">
                            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="14" viewBox="0 0 15 14" fill="none">
                                <path
                                    d="M10 3.125H2.38438L4.62625 0.88375L3.75 0L0 3.75L3.75 7.5L4.62625 6.61562L2.38625 4.375H10C10.9946 4.375 11.9484 4.77009 12.6517 5.47335C13.3549 6.17661 13.75 7.13044 13.75 8.125C13.75 9.11956 13.3549 10.0734 12.6517 10.7767C11.9484 11.4799 10.9946 11.875 10 11.875H5V13.125H10C11.3261 13.125 12.5979 12.5982 13.5355 11.6605C14.4732 10.7229 15 9.45108 15 8.125C15 6.79892 14.4732 5.52715 13.5355 4.58947C12.5979 3.65178 11.3261 3.125 10 3.125Z"
                                    fill="#6B6F89"
                                />
                            </svg>
                        </button>
                        <button className="ql-redo">
                            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="14" viewBox="0 0 15 14" fill="none">
                                <path
                                    d="M5 3.125H12.6156L10.3737 0.88375L11.25 0L15 3.75L11.25 7.5L10.3737 6.61562L12.6137 4.375H5C4.00544 4.375 3.05161 4.77009 2.34835 5.47335C1.64509 6.17661 1.25 7.13044 1.25 8.125C1.25 9.11956 1.64509 10.0734 2.34835 10.7767C3.05161 11.4799 4.00544 11.875 5 11.875H10V13.125H5C3.67392 13.125 2.40215 12.5982 1.46447 11.6605C0.526784 10.7229 0 9.45108 0 8.125C0 6.79892 0.526784 5.52715 1.46447 4.58947C2.40215 3.65178 3.67392 3.125 5 3.125Z"
                                    fill="#6B6F89"
                                />
                            </svg>
                        </button>
                    </span>
                )}
                <span className="ql-formats">
                    <button className="ql-bold" />
                    <button className="ql-italic" />
                    <button className="ql-underline" />
                    <button className="ql-strike" />
                </span>
                <span className="ql-formats">
                    <select className="ql-header" defaultValue="">
                        <option value="">Normal</option>
                        <option value="2">Heading</option>
                        <option value="3">Subheading</option>
                        <option value="4">Small Heading</option>
                    </select>
                </span>
                <span className="ql-formats">
                    <button className="ql-list" value="ordered" />
                    <button className="ql-list" value="bullet" />
                    <button className="ql-list" value="check" />
                </span>
                {!sidebar && (
                    <>
                        <span className="ql-formats">
                            <button className="ql-script" value="super" />
                            <button className="ql-script" value="sub" />
                        </span>
                        <span className="ql-formats">
                            <button className="ql-indent" value="-1" />
                            <button className="ql-indent" value="+1" />
                        </span>
                        <span className="ql-formats">
                            <button className="ql-direction" value="rtl" />
                        </span>
                        <span className="ql-formats">
                            <button className="ql-link" />
                            <button className="ql-image" />
                        </span>
                        <span className="ql-formats">
                            <button className="ql-clean" />
                        </span>
                    </>
                )}
            </div>
            {!sidebar && (
                <div className="d-flex align-items-center">
                    <Menu>
                        <MenuTrigger disableButtonEnhancement>
                            <button style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }} aria-label="Section actions">
                                <Wand20Regular color="var(--Brand-Secondary)" className="me-2" />
                            </button>
                        </MenuTrigger>
                        <MenuPopover>
                            <MenuList>
                                <MenuItem icon={<EditArrowBack16Regular color="var(--Brand-Secondary)" />} onClick={() => handleAIAction('rewrite')}>
                                    <span style={{ color: 'var(--Brand-Secondary)' }}>Rewrite</span>
                                </MenuItem>
                                <MenuItem icon={<TextAlignLeft20Regular color="var(--Brand-Secondary)" />} onClick={() => handleAIAction('summary')}>
                                    <span style={{ color: 'var(--Brand-Secondary)' }}>Summary</span>
                                </MenuItem>
                                <MenuItem icon={<TextBulletList20Regular color="var(--Brand-Secondary)" />} onClick={() => handleAIAction('keypoints')}>
                                    <span style={{ color: 'var(--Brand-Secondary)' }}>Keypoints</span>
                                </MenuItem>
                                <MenuItem icon={<TaskListSquareAdd20Regular color="var(--Brand-Secondary)" />} onClick={() => handleAIAction('tasks')}>
                                    <span style={{ color: 'var(--Brand-Secondary)' }}>Create Tasks</span>
                                </MenuItem>
                                {/* <MenuItem icon={<TableSimple20Regular color="var(--Brand-Secondary)" />} onClick={() => handleAIAction('table')}>
                                    <span style={{ color: 'var(--Brand-Secondary)' }}>Table</span>
                                </MenuItem> */}
                            </MenuList>
                        </MenuPopover>
                    </Menu>
                    <Menu>
                        <MenuTrigger disableButtonEnhancement>
                            <button style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }} aria-label="Section actions">
                                <MoreVertical20Regular color="var(--Neutral-Gray-6)" />
                            </button>
                        </MenuTrigger>
                        <MenuPopover>
                            <MenuList>
                                <MenuItem icon={<ArrowExport20Regular color="var(--Brand-Secondary)" />} onClick={() => handleMoveEntry()}>
                                    <span style={{ color: 'var(--Brand-Secondary)' }}>Move</span>
                                </MenuItem>
                                <MenuItem icon={<Delete20Regular color="var(--Brand-Secondary)" />} onClick={() => handleEntryDeleteClick()}>
                                    <span style={{ color: 'var(--Brand-Secondary)' }}>Delete entry</span>
                                </MenuItem>
                            </MenuList>
                        </MenuPopover>
                    </Menu>
                </div>
            )}
        </div>
    );
};

export default QuillToolbar;
